import { useCallback, useEffect, useRef, useState } from "react";
import { Box, ButtonBase, CircularProgress, Typography } from "@mui/material";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { verifyPin } from "../../services/me";
import {
  authenticateBiometric,
  biometricTypeLabel,
  ensureBiometricInited,
  markSessionUnlocked,
  type BiometricManager,
} from "../../utils";

interface AppLockScreenProps {
  /** Вызывается когда юзер успешно разблокировал приложение. */
  onUnlock: () => void;
}

/**
 * Полноэкранный «замок» — показывается ПЕРВЫМ, до основного UI, если
 * юзер подключил биометрию, но текущая сессия WebApp ещё не разблокирована.
 *
 * Разблокировка возможна двумя способами:
 *   1) Face ID / Touch ID — авто-триггерится при монтировании.
 *   2) PIN-код — на случай если биометрия сорвалась 3 раза подряд или
 *      юзер сам нажал «Ввести PIN».
 *
 * Внутри висит собственная 4-точечная клавиатура (без переиспользования
 * StepPin из онбординга, чтобы не тащить логику confirm/create).
 */
export function AppLockScreen({ onUnlock }: AppLockScreenProps) {
  const { t } = useTranslation("lock");
  const [bm, setBm] = useState<BiometricManager | null>(null);
  const [mode, setMode] = useState<"bio" | "pin">("bio");
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [shakeKey, setShakeKey] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [bioAttempted, setBioAttempted] = useState(false);

  // Инитим BM и запоминаем, чтобы label кнопки соответствовал типу
  // (Face ID vs. Touch ID). Если не поддерживается — сразу в PIN-режим.
  useEffect(() => {
    let cancelled = false;
    ensureBiometricInited().then((instance) => {
      if (cancelled) return;
      if (instance?.isBiometricTokenSaved) {
        setBm(instance);
      } else {
        setMode("pin");
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const doBiometric = useCallback(async () => {
    setError(null);
    setBioAttempted(true);
    const { ok, pin: storedPin } = await authenticateBiometric();
    if (!ok) return;
    if (!storedPin) {
      // Токен вернулся пустым — верифицировать нечего. Пропускаем в
      // приложение (Telegram уже подтвердил личность биометрией).
      markSessionUnlocked();
      onUnlock();
      return;
    }
    // Дополнительно валидируем на бэке — на случай если PIN сбрасывали
    // админом или менялся с другого устройства.
    setVerifying(true);
    try {
      const good = await verifyPin(storedPin);
      if (good) {
        markSessionUnlocked();
        onUnlock();
      } else {
        setError(t("pin_stale"));
        setMode("pin");
      }
    } finally {
      setVerifying(false);
    }
  }, [onUnlock, t]);

  // Автотриггер биометрии при первом рендере в bio-режиме.
  const triggeredRef = useRef(false);
  useEffect(() => {
    if (mode !== "bio" || !bm || triggeredRef.current) return;
    triggeredRef.current = true;
    doBiometric();
  }, [mode, bm, doBiometric]);

  const label = biometricTypeLabel(bm?.biometricType);
  const bioIcon =
    bm?.biometricType === "face"
      ? "solar:face-scan-circle-bold-duotone"
      : "solar:fingerprint-bold-duotone";

  // ---------- PIN keypad ----------

  const handleDigit = (d: string) => {
    if (verifying) return;
    setError(null);
    setPin((prev) => (prev.length < 4 ? prev + d : prev));
  };
  const handleDelete = () => {
    if (verifying) return;
    setError(null);
    setPin((prev) => prev.slice(0, -1));
  };
  const handleClear = () => {
    if (verifying) return;
    setError(null);
    setPin("");
  };

  // Как только набрали 4 цифры — уходим на верификацию.
  useEffect(() => {
    if (pin.length !== 4 || verifying) return;
    let cancelled = false;
    (async () => {
      setVerifying(true);
      const ok = await verifyPin(pin);
      if (cancelled) return;
      if (ok) {
        markSessionUnlocked();
        onUnlock();
      } else {
        setError(t("incorrect_pin"));
        setShakeKey((k) => k + 1);
        setTimeout(() => {
          if (!cancelled) setPin("");
        }, 380);
      }
      setVerifying(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [pin, onUnlock, verifying, t]);

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        inset: 0,
        zIndex: 100010,
        bgcolor: theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "22px",
        padding: "24px",
        overflow: "hidden",
      })}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
      >
        <Box
          sx={(theme) => ({
            width: 72,
            height: 72,
            borderRadius: "22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.05)",
            color: theme.palette.primary.main,
          })}
        >
          <Icon icon="solar:lock-keyhole-bold-duotone" width={38} height={38} />
        </Box>
      </motion.div>

      <Box sx={{ textAlign: "center", display: "grid", gap: "6px" }}>
        <Typography sx={{ fontSize: "22px", fontWeight: 700 }}>
          {t("title")}
        </Typography>
        <Typography
          sx={{ fontSize: "13px", color: "text.secondary", maxWidth: 300 }}
        >
          {mode === "bio" ? t("confirm_via", { label }) : t("subtitle")}
        </Typography>
      </Box>

      {/* PIN keypad */}
      <motion.div
        key={shakeKey}
        animate={error ? { x: [0, -12, 12, -8, 8, -4, 4, 0] } : { x: 0 }}
        transition={{ duration: 0.4 }}
        style={{ display: "flex", gap: 18, marginTop: 4 }}
      >
        {[0, 1, 2, 3].map((i) => {
          const filled = i < pin.length;
          return (
            <motion.div
              key={i}
              animate={filled ? { scale: [1, 1.35, 1] } : { scale: 1 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <Box
                sx={(theme) => ({
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: `2px solid ${
                    error
                      ? theme.palette.error.main
                      : theme.palette.primary.main
                  }`,
                  bgcolor: filled
                    ? error
                      ? theme.palette.error.main
                      : theme.palette.primary.main
                    : "transparent",
                  transition:
                    "background 0.15s, border-color 0.15s, box-shadow 0.2s",
                })}
              />
            </motion.div>
          );
        })}
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "error.main",
                textAlign: "center",
                lineHeight: 1.3,
              }}
            >
              {error}
            </Typography>
          </motion.div>
        )}
      </AnimatePresence>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 12,
          width: "100%",
          maxWidth: 300,
        }}
      >
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <KeypadButton key={d} onClick={() => handleDigit(d)} disabled={verifying}>
            {d}
          </KeypadButton>
        ))}
        {bm?.isBiometricTokenSaved ? (
          // Кнопка биометрии слева от нуля — можно снова попытаться,
          // если первый автозапуск отклонили.
          <KeypadButton
            onClick={doBiometric}
            disabled={verifying}
            aria-label={label}
          >
            <Icon icon={bioIcon} width={26} height={26} />
          </KeypadButton>
        ) : (
          <KeypadButton onClick={handleClear} disabled={verifying || pin.length === 0}>
            <Icon icon="solar:refresh-linear" width={22} height={22} />
          </KeypadButton>
        )}
        <KeypadButton onClick={() => handleDigit("0")} disabled={verifying}>
          0
        </KeypadButton>
        <KeypadButton
          onClick={handleDelete}
          disabled={verifying || pin.length === 0}
        >
          <Icon icon="solar:backspace-linear" width={22} height={22} />
        </KeypadButton>
      </Box>

      {verifying && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "text.secondary",
            fontSize: "13px",
          }}
        >
          <CircularProgress size={14} />
          {t("verifying")}
        </Box>
      )}

      {bm?.isBiometricTokenSaved && bioAttempted && mode === "bio" && (
        <ButtonBase
          onClick={() => setMode("pin")}
          sx={{
            padding: "8px 14px",
            fontSize: "13px",
            color: "text.secondary",
            borderRadius: "12px",
          }}
        >
          {t("use_pin")}
        </ButtonBase>
      )}
    </Box>
  );
}

// ---------- keypad button (локальная копия онбординговой стилистики) ----------

function KeypadButton({
  children,
  onClick,
  disabled,
  ...rest
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  "aria-label"?: string;
}) {
  return (
    <motion.div
      whileTap={disabled ? undefined : { scale: 0.9 }}
      transition={{ type: "spring", stiffness: 550, damping: 26 }}
    >
      <ButtonBase
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        aria-label={rest["aria-label"]}
        sx={(theme) => ({
          width: "100%",
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          fontSize: "24px",
          fontWeight: 500,
          color: "text.primary",
          bgcolor: "secondary.main",
          border:
            theme.palette.mode === "light"
              ? "1px solid rgba(0, 0, 0, 0.05)"
              : "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 0.15s",
          opacity: disabled ? 0.4 : 1,
        })}
      >
        {children}
      </ButtonBase>
    </motion.div>
  );
}
